import {groupByUniqueKey} from '@benchling/js-utils';
import React from 'react';
import ReactDOM from 'react-dom/client';

import TranslationTableTabState from 'analyses/TranslationTabState';
import * as analytics from 'analytics/Analytics';
import {pluralize} from 'BenchlingUtil';
import {CodonTranslationTable} from 'biojs/BioConstants';
import BufferTabView from 'buffers/BufferTabView';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {BulkEditTranslationRegionGroupData, bulkEditTranslationRegionGroups} from 'molbio/dao/SequenceDAO';
import ExpandedTranslationTab from 'molbio/translations/ExpandedTranslationTab';
import {
  confirmTranslationLinkInvalidationRisk,
  getTranslationRegionGroupForTranslation,
  getTranslationRegionGroupsForSequence,
  updateModelsAfterTranslationChange,
} from 'molbio/translations/TranslationUtil';
import notifications from 'notifications';
import {renderToDOM, unmountRootsAsync} from 'redux-stores/ReduxDOMRender';

class TranslationTabView extends BufferTabView<TranslationTableTabState> {
  reactRoot: ReactDOM.Root | null = null;

  static override initClass(): void {
    this.prototype.className = 'buffer-tab';
  }

  override initialize(): void {
    const sequence = this.model.sequence();
    const _sequence = sequence._sequence();
    this.listenTo(
      sequence,
      'change:writable change:permissions change:currentVersion change:translations',
      this.render.bind(this)
    );
    this.listenTo(sequence.translations(), 'add remove reset change:_translation', this.render.bind(this));
    this.listenTo(_sequence, 'change:startIndex change:locked', this.render.bind(this));
    this.listenTo(_sequence, 'change:translationGroups', this.render.bind(this));
  }

  async deleteTranslations(translationIds: Array<number>, successMessage?: string): Promise<void> {
    const sequence = this.model.sequence();
    const tagSchema = sequence.tagSchema();
    const translationRegionGroupsToDelete = sequence
      .translations()
      .filter(({id}) => translationIds.includes(id))
      .map((t) => getTranslationRegionGroupForTranslation(sequence, t));
    if (
      !(await confirmTranslationLinkInvalidationRisk(
        tagSchema,
        translationRegionGroupsToDelete.some(
          (g) => g.codonTranslationTable !== CodonTranslationTable.STANDARD
        ),
        translationRegionGroupsToDelete.some((g) => g.coerceStartCodonToMethionine),
        true,
        translationRegionGroupsToDelete.length
      ))
    ) {
      return;
    }
    this.model
      .deleteTranslations(translationIds)
      .done(({message}: {message: string; deleted: number}) => {
        notifications.success(successMessage ?? message);
        analytics.track('translation', 'deleted_translations', {
          product: ANALYTICS_MODULE.MOLBIO,
          sequence_id: this.model.sequence().id,
          source: 'translations-table',
          translation_ids: translationIds,
        });
      })
      .fail(notifications.notifyError);
  }

  // Edit properties translation groups and their regions in bulk and updates
  // the corresponding Backbone models accordingly
  async bulkEditTranslations(data: Array<BulkEditTranslationRegionGroupData>): Promise<void> {
    const sequence = this.model.sequence();
    const tagSchema = sequence.tagSchema();
    const translationGroupById = groupByUniqueKey(
      sequence.translationGroups(),
      (translationGroup) => translationGroup.id
    );
    const modifiedNonStandardCodonTranslationTable = data.some((bulkEditTranslationData) => {
      if (bulkEditTranslationData.id == null) {
        return false;
      }
      const originalTranslationGroupData = translationGroupById[bulkEditTranslationData.id];
      const codonTranslationTable = bulkEditTranslationData?.properties?.codonTranslationTable;
      return (
        codonTranslationTable != null &&
        codonTranslationTable !== originalTranslationGroupData.codonTranslationTable &&
        originalTranslationGroupData.codonTranslationTable !== CodonTranslationTable.STANDARD
      );
    });
    const modifiedCoerceStartCodonToMethionine = data.some((bulkEditTranslationData) => {
      if (bulkEditTranslationData.id == null) {
        return false;
      }
      const originalTranslationGroupData = translationGroupById[bulkEditTranslationData.id];
      const coerceStartCodonToMethionine = bulkEditTranslationData?.properties?.coerceStartCodonToMethionine;
      return (
        coerceStartCodonToMethionine != null &&
        coerceStartCodonToMethionine !== originalTranslationGroupData.coerceStartCodonToMethionine &&
        originalTranslationGroupData.coerceStartCodonToMethionine
      );
    });
    if (
      !(await confirmTranslationLinkInvalidationRisk(
        tagSchema,
        modifiedNonStandardCodonTranslationTable,
        modifiedCoerceStartCodonToMethionine,
        false,
        data.length
      ))
    ) {
      return;
    }
    const {updated} = await bulkEditTranslationRegionGroups(sequence.id, data);
    const updatedGroups = updated.map(({translationGroup}) => translationGroup);
    const updatedRegions = updated.flatMap(({translationRegions}) => translationRegions);
    updateModelsAfterTranslationChange(sequence, updatedGroups, updatedRegions, []);
    notifications.success(
      `Successfully saved ${updated.length} ${pluralize(updated.length, 'translation')}.`
    );
    analytics.track('translation', 'updated_translations', {
      product: ANALYTICS_MODULE.MOLBIO,
      sequence_id: sequence.id,
      source: 'translations-table',
      translation_group_ids: updatedGroups.map(({id}) => id),
      translation_ids: updatedRegions.map(({id}) => id),
    });
  }

  override removeAsync(callback: () => void): BufferTabView<TranslationTableTabState> {
    unmountRootsAsync([this.reactRoot], () => {
      this.reactRoot = null;
      super.remove();
      callback();
    });
    return this;
  }

  override render(): this {
    const sequence = this.model.sequence();
    this.reactRoot = renderToDOM(
      <ExpandedTranslationTab
        translationRegionGroups={getTranslationRegionGroupsForSequence(sequence)}
        sequenceStartIndex={sequence.startIndex()}
        sequenceLength={sequence.length()}
        isSingleStranded={sequence.isSingleStranded()}
        allowFeatureEdits={sequence.allowFeatureEdits()}
        deleteTranslations={(translationIds: Array<number>, stringMessage: string) => {
          this.deleteTranslations(translationIds, stringMessage);
        }}
        bulkEditTranslations={(data: Array<BulkEditTranslationRegionGroupData>) => {
          this.bulkEditTranslations(data);
        }}
      />,
      this.el,
      this.reactRoot
    );
    return this;
  }
}

TranslationTabView.initClass();

export {TranslationTabView};
